import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Disc3 } from "lucide-react";
import { api, fileUrl, HERO_IMAGE, FALLBACK_COVER } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ReleaseCard, PlayButton, EmptyState } from "@/components/site/ReleaseCard";
import { SocialLinks } from "@/components/site/Social";
import { NewsletterSection } from "@/components/site/Newsletter";
import { PioneerDeck } from "@/components/site/PioneerDeck";
import { Reveal } from "@/components/site/Reveal";
import { TrendingTracks } from "@/components/site/TrendingTracks";

const HOST = window.location.host;

const Marquee = () => (
  <div className="border-y border-white/10 bg-emerald-500 text-black overflow-hidden py-3">
    <div className="marquee flex whitespace-nowrap font-display font-black uppercase text-sm tracking-tight w-max">
      {Array.from({ length: 2 }).map((_, k) => (
        <span key={k} className="flex">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="px-6 flex items-center gap-6">The Groove Anomaly records <Disc3 className="h-4 w-4" /> <span data-testid={k === 0 && i === 0 ? "marquee-host" : undefined}>{HOST}</span> <Disc3 className="h-4 w-4" /></span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

export default function Home() {
  const { settings } = useSettings();
  const [releases, setReleases] = useState([]);
  useEffect(() => { api.get("/releases").then((r) => setReleases(r.data)).catch(() => {}); }, []);
  const featured = releases.find((r) => r.featured) || releases[0];

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <img src={fileUrl(settings.hero_image) || HERO_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#151827] via-[#151827]/60 to-[#151827]/20" />
        <div className="absolute -top-32 -right-32 h-[30rem] w-[30rem] rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-40 h-[26rem] w-[26rem] rounded-full bg-hot/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pt-28 pb-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
          <p className="font-mono-tga text-sm uppercase tracking-[0.35em] text-emerald-400 rise" data-testid="hero-overline">Etichetta discografica indipendente</p>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] mt-6 text-5xl sm:text-7xl lg:text-8xl rise drop-shadow-[0_6px_30px_rgba(0,0,0,0.5)]" style={{ animationDelay: "100ms" }} data-testid="hero-title">
            The Groove<br /><span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-hot bg-clip-text text-transparent">Anomaly</span><br /><span className="text-zinc-300 font-semibold lowercase">records</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg md:text-xl text-zinc-200 rise" style={{ animationDelay: "200ms" }}>{settings.tagline}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4 rise" style={{ animationDelay: "300ms" }}>
            <Link to="/uscite" data-testid="hero-catalog-btn" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#151827] px-7 py-3.5 font-bold hover:bg-emerald-400 hover:gap-3 hover:shadow-[0_0_30px_rgba(31,232,150,0.6)] transition-[gap,background-color,box-shadow] duration-300">
              Esplora le uscite <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="font-mono-tga text-xs tracking-[0.2em] border border-white/25 bg-white/5 backdrop-blur rounded-full px-5 py-3 text-zinc-200" data-testid="hero-domain">{HOST}</span>
            <SocialLinks prefix="hero-social" />
          </div>
          </div>
          <div className="flex justify-center lg:justify-end rise" style={{ animationDelay: "250ms" }}>
            <PioneerDeck featured={featured} />
          </div>
        </div>
      </section>

      <Marquee />

      {featured && (
        <Reveal as="section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-[1fr_1.2fr] gap-12 items-center">
          <div data-testid="featured-release" className="contents">
          <Link to={`/uscite/${featured.id}`} className="relative aspect-square border border-white/15 overflow-hidden group rounded-2xl lift">
            <img src={fileUrl(featured.cover_url) || FALLBACK_COVER} alt={featured.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </Link>
          <div>
            <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-400">Ultima uscita {featured.catalog_number && `· ${featured.catalog_number}`}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tighter mt-4">{featured.title}</h2>
            <p className="text-lg text-zinc-300 mt-2">{featured.artist_name}</p>
            <p className="text-sm text-zinc-400 mt-6 max-w-lg line-clamp-4">{featured.description}</p>
            <div className="mt-8 flex items-center gap-4">
              <PlayButton release={featured} trackIdx={null} testid="featured-play-btn" className="h-14 w-14" />
              <Link to={`/uscite/${featured.id}`} className="font-mono-tga text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-emerald-400" data-testid="featured-details-link">Dettagli uscita →</Link>
            </div>
          </div>
          </div>
        </Reveal>
      )}

      <TrendingTracks releases={releases} />

      <Reveal as="section" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-400">Catalogo</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight mt-3">Uscite recenti</h2>
          </div>
          <Link to="/uscite" data-testid="home-all-releases-link" className="font-mono-tga text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-emerald-400">Tutte →</Link>
        </div>
        {releases.length ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
            {releases.slice(0, 8).map((r, i) => <ReleaseCard key={r.id} release={r} i={i} />)}
          </div>
        ) : (
          <EmptyState testid="home-empty-releases" title="Il catalogo sta per arrivare" text="Le prime uscite di The Groove Anomaly records saranno pubblicate a breve." />
        )}
      </Reveal>

      <Reveal><NewsletterSection /></Reveal>
    </div>
  );
}
